import { CommonModule } from '@angular/common';
import { Component, DestroyRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { CompanySelectionService } from '@core/service/company-selection.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { DateTimePickerFieldComponent } from '@shared/components/date-time-picker-field/date-time-picker-field.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { environment } from '../../../environments/environment';
import { resolveApiUrl } from '../../../environments/resolve-api-url';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { AttendanceEventRecord } from '../attendance-event/attendance-event.interface';

interface AttendanceEventsResponse {
  data?: AttendanceEventRecord[];
  pagination?: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}

interface CompaniesResponse { data?: CompaniesRecord[]; items?: CompaniesRecord[]; results?: CompaniesRecord[]; companiesRecords?: CompaniesRecord[]; }

interface EvidenceViewModel extends AttendanceEventRecord {
  employeeLabel: string;
  eventAtLabel: string;
  sourceLabel: string;
  statusLabel: string;
  faceStatusLabel: string;
  eventTypeLabel: string;
  imageUrl: string;
  imageFailed: boolean;
}

@Component({
  selector: 'app-facial-evidence',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    CompanySelectComponent,
    DateTimePickerFieldComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSelectModule,
    MatTooltipModule,
    LoadingStateComponent,
    TranslateModule,
  ],
  templateUrl: './facial-evidence.component.html',
  styleUrl: './facial-evidence.component.scss',
})
export class FacialEvidenceComponent implements OnInit, OnDestroy {
  evidences: EvidenceViewModel[] = [];
  employees: EmployeeRecord[] = [];
  companies: CompaniesRecord[] = [];
  selectedEvidence: EvidenceViewModel | null = null;
  isLoading = true;
  isLoadingEmployees = false;
  currentPage = 1;
  pageSize = 12;
  totalItems = 0;
  employeeSearchTerm = '';

  filters = {
    companiesRecordId: '',
    employeeId: '',
    source: [] as string[],
    status: '',
    dateFrom: '',
    dateTo: '',
  };

  readonly sourceOptions = ['mobile', 'kiosk', 'web'];
  readonly statusOptions = ['pending_analysis', 'analyzed', 'rejected'];

  private readonly apiService = inject(AuthorizedApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly companySelection = inject(CompanySelectionService);
  private readonly apiBaseUrl = resolveApiUrl(environment.apiUrl);
  private employeeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private readonly evidenceObjectUrls = new Set<string>();

  ngOnInit(): void {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.evidences = this.evidences.map((event) => this.normalizeEvent(event));
        if (this.selectedEvidence) {
          this.selectedEvidence = this.normalizeEvent(this.selectedEvidence);
        }
      });
    void this.initialize();
  }

  ngOnDestroy(): void {
    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }
    this.releaseEvidenceObjectUrls();
    document.body.classList.remove('facial-evidence-modal-open');
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (this.selectedEvidence) {
      this.closeEvidence();
    }
  }

  get currentPageWithPhoto(): number {
    return this.evidences.filter((event) => !!event.imageUrl && !event.imageFailed).length;
  }

  get currentPagePending(): number {
    return this.evidences.filter((event) => event.status === 'pending_analysis').length;
  }

  get currentPagePassed(): number {
    return this.evidences.filter((event) => event.faceValidationStatus === 'passed').length;
  }

  get sourceFilterLabel(): string {
    if (!this.filters.source.length || this.filters.source.length === this.sourceOptions.length) {
      return this.t('facialEvidences.filters.allSources');
    }
    return this.filters.source
      .map((source) => this.t(`facialEvidences.sources.${source}`))
      .join(', ');
  }

  async initialize(): Promise<void> {
    await this.loadCompanies();
    await Promise.all([this.loadEmployees(), this.loadEvidences()]);
  }

  async onCompanyChanged(companyId: string): Promise<void> {
    this.filters.companiesRecordId = companyId || '';
    this.filters.employeeId = '';
    this.employeeSearchTerm = '';
    this.companySelection.rememberSelection(this.filters.companiesRecordId);
    this.currentPage = 1;
    await this.loadEmployees();
    await this.loadEvidences();
  }

  async loadEvidences(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceEventsResponse>('attendance/events', {
          params: this.buildRequestParams(),
        })
      );
      this.releaseEvidenceObjectUrls();
      this.evidences = (response?.data || []).map((event) => this.normalizeEvent(event));
      await this.loadAuthorizedEvidenceImages(this.evidences);
      this.totalItems = response?.pagination?.total ?? this.evidences.length;
    } catch (error) {
      console.error('Error loading facial evidences', error);
      this.evidences = [];
      this.totalItems = 0;
      this.snackBar.open(
        extractApiErrorMessage(error, this.t('facialEvidences.notifications.loadError')),
        '',
        {
          duration: 3500,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: 'snackbar-danger',
        }
      );
    } finally {
      this.isLoading = false;
    }
  }

  async loadEmployees(search = ''): Promise<void> {
    const isInitialLoad = !search && !this.employees.length;
    if (isInitialLoad) {
      this.isLoadingEmployees = true;
    }
    try {
      const selectedEmployee = this.employees.find(
        (employee) => employee.employeeId === this.filters.employeeId
      );
      const response = await firstValueFrom(
        this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees', {
          params: {
            page: 1,
            perPage: search ? 50 : 100,
            isActive: 'true',
            ...(this.filters.companiesRecordId ? { companiesRecordId: this.filters.companiesRecordId } : {}),
            ...(search ? { search } : {}),
          },
        })
      );
      const records = Array.isArray(response)
        ? response
        : response.data || response.items || response.results || response.employees || [];
      const nextEmployees = records
        .filter((employee) => employee.isActive !== false)
        .sort((left, right) => this.employeeName(left).localeCompare(this.employeeName(right)));
      this.employees = selectedEmployee
        && !nextEmployees.some((employee) => employee.employeeId === selectedEmployee.employeeId)
          ? [selectedEmployee, ...nextEmployees]
          : nextEmployees;
    } catch (error) {
      console.error('Error loading employees for facial evidences', error);
      if (!this.employees.length) {
        this.employees = [];
      }
    } finally {
      if (isInitialLoad) {
        this.isLoadingEmployees = false;
      }
    }
  }

  onEmployeeSearchInput(event: Event): void {
    this.employeeSearchTerm = (event.target as HTMLInputElement).value.trim();
    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }
    this.employeeSearchDebounceHandle = setTimeout(() => {
      void this.loadEmployees(this.employeeSearchTerm);
    }, 250);
  }

  onEmployeeSelectOpened(opened: boolean): void {
    if (opened || !this.employeeSearchTerm) {
      return;
    }
    this.employeeSearchTerm = '';
    void this.loadEmployees();
  }

  applyFilters(): void {
    this.currentPage = 1;
    void this.loadEvidences();
  }

  onDateFilterChange(changed: 'dateFrom' | 'dateTo'): void {
    if (this.filters.dateFrom && this.filters.dateTo && this.filters.dateFrom > this.filters.dateTo) {
      if (changed === 'dateFrom') {
        this.filters.dateTo = this.filters.dateFrom;
      } else {
        this.filters.dateFrom = this.filters.dateTo;
      }
    }
    this.applyFilters();
  }

  refreshEvidences(): void {
    void this.loadEvidences();
  }

  clearFilters(): void {
    this.filters = {
      companiesRecordId: this.companies.length === 1 ? this.companies[0].companiesRecordId : '',
      employeeId: '',
      source: [],
      status: '',
      dateFrom: '',
      dateTo: '',
    };
    this.companySelection.rememberSelection(this.filters.companiesRecordId);
    this.currentPage = 1;
    void this.loadEmployees().then(() => this.loadEvidences());
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    void this.loadEvidences();
  }

  openEvidence(event: EvidenceViewModel): void {
    if (!event.imageUrl || event.imageFailed) {
      return;
    }
    this.selectedEvidence = event;
    document.body.classList.add('facial-evidence-modal-open');
  }

  closeEvidence(): void {
    this.selectedEvidence = null;
    document.body.classList.remove('facial-evidence-modal-open');
  }

  markImageFailed(event: EvidenceViewModel): void {
    event.imageFailed = true;
  }

  trackEvidence(_: number, event: EvidenceViewModel): string {
    return event.attendanceEventId;
  }

  statusTone(status?: string | null): string {
    switch (status) {
      case 'analyzed':
        return 'success';
      case 'rejected':
        return 'danger';
      case 'pending_analysis':
        return 'warning';
      default:
        return 'neutral';
    }
  }

  faceTone(status?: string | null): string {
    switch (status) {
      case 'passed':
        return 'success';
      case 'failed':
      case 'rejected':
        return 'danger';
      case 'pending':
      case 'pending_analysis':
        return 'warning';
      default:
        return 'neutral';
    }
  }

  private buildRequestParams(
    page = this.currentPage,
    perPage = this.pageSize
  ): Record<string, string | number> {
    const source = this.filters.source.length === 1 ? this.filters.source[0] : '';
    return {
      page,
      perPage,
      sort: '-eventAt',
      hasFaceEvidence: 'true',
      ...(this.filters.companiesRecordId ? { companiesRecordId: this.filters.companiesRecordId } : {}),
      ...(this.filters.employeeId ? { employeeId: this.filters.employeeId } : {}),
      ...(source ? { source } : {}),
      ...(this.filters.status ? { status: this.filters.status } : {}),
      ...(this.filters.dateFrom ? { dateFrom: this.toDayStartIso(this.filters.dateFrom) } : {}),
      ...(this.filters.dateTo ? { dateTo: this.toDayEndIso(this.filters.dateTo) } : {}),
    };
  }

  private async loadCompanies(): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', { params: { isActive: 'true' } }));
      this.companies = (Array.isArray(response) ? response : response.data || response.items || response.results || response.companiesRecords || [])
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
      this.filters.companiesRecordId = this.companySelection.resolveSharedSelection(this.companies, this.filters.companiesRecordId);
    } catch (error) {
      console.error('Error loading companies for facial evidences', error);
      this.companies = [];
      this.filters.companiesRecordId = '';
    }
  }

  private toDayStartIso(day: string): string {
    const [year, month, date] = day.split('-').map(Number);
    return new Date(year, month - 1, date, 0, 0, 0, 0).toISOString();
  }

  private toDayEndIso(day: string): string {
    const [year, month, date] = day.split('-').map(Number);
    return new Date(year, month - 1, date, 23, 59, 59, 999).toISOString();
  }

  private normalizeEvent(event: AttendanceEventRecord): EvidenceViewModel {
    const source = String(event.source || '').toLowerCase();
    const status = String(event.status || '').toLowerCase();
    const faceStatus = String(event.faceValidationStatus || '').toLowerCase();
    const eventType = String(event.normalizedEventType || event.eventType || '').toLowerCase();
    return {
      ...event,
      source,
      status,
      faceValidationStatus: faceStatus,
      employeeLabel: event.employeeName || event.employeeNumber || this.t('facialEvidences.unknownEmployee'),
      eventAtLabel: this.formatDateTime(event.eventAt || event.eventAtUtc, event.timezone),
      sourceLabel: this.t(`facialEvidences.sources.${source || 'unknown'}`),
      statusLabel: this.t(`facialEvidences.statuses.${status || 'unknown'}`),
      faceStatusLabel: this.t(`facialEvidences.faceStatuses.${faceStatus || 'unknown'}`),
      eventTypeLabel: this.t(`facialEvidences.eventTypes.${eventType || 'unknown'}`),
      imageUrl: 'imageUrl' in event ? String(event.imageUrl || '') : '',
      imageFailed: 'imageFailed' in event ? Boolean(event.imageFailed) : false,
    };
  }

  private async loadAuthorizedEvidenceImages(events: EvidenceViewModel[]): Promise<void> {
    await Promise.all(events.map(async (event) => {
      const viewUrl = this.resolveEvidenceViewUrl(event.faceEvidence?.viewUrl);
      if (!viewUrl) {
        event.imageFailed = true;
        return;
      }

      try {
        const response = await firstValueFrom(this.apiService.getBlob(viewUrl));
        const blob = response instanceof HttpResponse ? response.body : response;
        if (!(blob instanceof Blob) || blob.size === 0) {
          event.imageFailed = true;
          return;
        }

        const objectUrl = URL.createObjectURL(blob);
        this.evidenceObjectUrls.add(objectUrl);
        event.imageUrl = objectUrl;
        event.imageFailed = false;
      } catch (error) {
        console.error('Error loading authorized facial evidence', error);
        event.imageUrl = '';
        event.imageFailed = true;
      }
    }));
  }

  private releaseEvidenceObjectUrls(): void {
    this.evidenceObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.evidenceObjectUrls.clear();
  }

  private employeeName(employee: EmployeeRecord): string {
    return employee.fullName || employee.employeeNumber || employee.employeeId;
  }

  private resolveEvidenceViewUrl(viewUrl?: string | null): string {
    const normalizedViewUrl = String(viewUrl || '').trim();
    if (!normalizedViewUrl) {
      return '';
    }

    try {
      return new URL(normalizedViewUrl, `${this.apiBaseUrl.replace(/\/+$/, '')}/`).toString();
    } catch {
      return `${this.apiBaseUrl.replace(/\/+$/, '')}/${normalizedViewUrl.replace(/^\/+/, '')}`;
    }
  }

  private formatDateTime(value?: string | null, timezone?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat(
      this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX',
      {
        timeZone: timezone || 'America/Cancun',
        dateStyle: 'medium',
        timeStyle: 'short',
      }
    ).format(date);
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }
}
